import { useCallback, useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { Request } from '@omnea-app/requests/requestsHttpClient'

type Actions = 'editFollowers' | 'editDocuments' | 'addCustomFields' | 'pauseRequest' | 'resumeRequest'

export type CanPerformActionsRequest = Pick<Request, 'participants'> & {
  derivedFields: Pick<Request['derivedFields'], 'currentGroupAssignees' | 'currentUserAssignees'>
}

export const useCanPerformRequestAction = (request: CanPerformActionsRequest | undefined) => {
  const { user } = useAuthenticatedUser()

  const isAttachedToRequest = useMemo(() => {
    if (!request) {
      return false
    }
    const isRequestor = request?.participants?.requestor?.id === user.id
    const isProjectOwner = request?.participants?.projectOwner?.id === user.id
    const isBusinessOwner = request?.participants?.businessOwner?.id === user.id
    const isUserAssignee = request?.derivedFields?.currentUserAssignees?.some((a) => a.id === user.id)
    const isGroupAssignee = request?.derivedFields?.currentGroupAssignees?.some((g) => g.userIds.includes(user.id))
    const isAssignee = isUserAssignee || isGroupAssignee

    return isRequestor || isProjectOwner || isBusinessOwner || isAssignee
  }, [user.id, request])

  return useCallback(
    (action: Actions): boolean => {
      // only isViewOnly requires special handling atm
      if (!user.isViewOnly) {
        return true
      }
      switch (action) {
        case 'editDocuments':
        case 'editFollowers':
        case 'pauseRequest':
        case 'resumeRequest':
          return isAttachedToRequest
        case 'addCustomFields':
          return false
        default:
          return false
      }
    },
    [user.isViewOnly, isAttachedToRequest]
  )
}
